import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import {
  createCompanyVenue,
  deleteCompanyVenue,
  getApiErrorMessage,
  listCompanyVenues,
  listDepartments,
  updateCompanyVenue,
} from "../../services/adminCompanyService.js";

const EMPTY_FORM = {
  name: "",
  address: "",
  linkMap: "",
  iddepartamento: "",
};

const CompanyVenuesTab = ({ idcompany }) => {
  const [venues, setVenues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadVenues = useCallback(async () => {
    if (!idcompany) return;
    setLoading(true);
    try {
      setVenues(await listCompanyVenues(idcompany, true));
    } catch (error) {
      console.error("Error al cargar sedes", error);
      toast.error(getApiErrorMessage(error, "No se pudieron cargar las sedes."));
    } finally {
      setLoading(false);
    }
  }, [idcompany]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  useEffect(() => {
    listDepartments()
      .then(setDepartments)
      .catch((error) => console.error("Error al cargar departamentos", error));
  }, []);

  const filteredVenues = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return venues;
    return venues.filter((venue) =>
      [venue.name, venue.address, venue.department?.name].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [search, venues]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const beginEdit = (venue) => {
    setEditing(venue);
    setForm({
      name: venue.name || "",
      address: venue.address || "",
      linkMap: venue.linkMap || "",
      iddepartamento:
        venue.iddepartamento != null ? String(venue.iddepartamento) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre de la sede es obligatorio.");
      return;
    }

    const payload = {
      idcompany,
      name: form.name.trim(),
      address: form.address.trim() || null,
      linkMap: form.linkMap.trim() || null,
      iddepartamento: form.iddepartamento
        ? Number(form.iddepartamento)
        : null,
      ...(editing ? { state: Number(editing.state ?? 1) } : {}),
    };

    setSaving(true);
    try {
      if (editing) {
        await updateCompanyVenue({ idsede: editing.idsede, payload });
        toast.success("Sede actualizada correctamente.");
      } else {
        await createCompanyVenue(payload);
        toast.success("Sede creada correctamente.");
      }
      resetForm();
      await loadVenues();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo guardar la sede."));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (venue) => {
    const isActive = Number(venue.state) !== 0;
    const action = isActive ? "desactivar" : "reactivar";
    if (!window.confirm(`¿Desea ${action} ${venue.name}?`)) return;

    setSaving(true);
    try {
      if (isActive) {
        await deleteCompanyVenue(venue.idsede);
      } else {
        await updateCompanyVenue({
          idsede: venue.idsede,
          payload: { idcompany, state: 1 },
        });
      }
      toast.success(`Sede ${isActive ? "desactivada" : "reactivada"}.`);
      if (editing?.idsede === venue.idsede) resetForm();
      await loadVenues();
    } catch (error) {
      toast.error(getApiErrorMessage(error, `No se pudo ${action} la sede.`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {editing ? "Editar sede" : "Nueva sede"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              La sede quedará asociada a la organización.
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800"
            >
              Nueva
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Nombre *
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Dirección
            <input
              name="address"
              value={form.address}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Enlace de Google Maps
            <input
              type="url"
              name="linkMap"
              value={form.linkMap}
              onChange={handleChange}
              placeholder="https://maps.google.com/..."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Departamento
            <select
              name="iddepartamento"
              value={form.iddepartamento}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Sin departamento</option>
              {departments.map((department) => (
                <option
                  key={department.iddepartamento}
                  value={department.iddepartamento}
                >
                  {department.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear sede"}
            </button>
          </div>
        </form>
      </section>

      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            Sedes de la Organización
          </h2>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar sede..."
            className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
        ) : filteredVenues.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            No hay sedes registradas.
          </div>
        ) : (
          <div className="space-y-2">
            {filteredVenues.map((venue) => {
              const active = Number(venue.state) !== 0;
              return (
                <article
                  key={venue.idsede}
                  className={`flex flex-col justify-between gap-3 rounded-lg border p-3 sm:flex-row sm:items-center ${
                    active
                      ? "border-slate-200 bg-white"
                      : "border-slate-200 bg-slate-50 opacity-70"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-semibold text-slate-800">
                        {venue.name}
                      </h3>
                      {!active && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-slate-500">
                      {venue.address || "Sin dirección"}
                    </p>
                    {venue.department?.name && (
                      <p className="text-xs text-slate-400">{venue.department.name}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => beginEdit(venue)}
                      className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(venue)}
                      disabled={saving}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        active
                          ? "border-red-200 text-red-600 hover:bg-red-50"
                          : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      {active ? "Desactivar" : "Reactivar"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default CompanyVenuesTab;

